import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import { Input } from "../components/ui/input";

const MIN_DAYS = 1;
const MAX_DAYS = 365;

// Paramètres › Corbeille — how long a deleted item can be restored before it is erased for good (Ordre 28, 30 days by default).
// settings:view to read, settings:manage to change. A change applies to what is deleted afterwards: each item keeps the date of
// erasure it was given when it went to the trash.
export default function SettingsDeletion() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [value, setValue] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = (settings) => { setSaved(settings); setValue(String(settings.retention_days)); };
  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/deletion");
      apply(data.settings);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const n = Number(value);
  const invalid = !Number.isInteger(n) || n < MIN_DAYS || n > MAX_DAYS || value.trim() === "";
  const dirty = saved != null && String(saved.retention_days) !== value;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/deletion", { retention_days: n });
      apply(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-deletion">
      <SectionHeader title="Corbeille" description="Le temps pendant lequel un élément supprimé peut être restauré." />
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <label htmlFor="deletion-days" className="text-sm font-medium text-slate-900">Durée de conservation</label>
        <div className="flex items-center gap-2">
          <Input id="deletion-days" type="number" min={MIN_DAYS} max={MAX_DAYS} value={value} disabled={!canManage} className="w-24"
            onChange={(e) => setValue(e.target.value)} aria-invalid={invalid} data-testid="deletion-days" />
          <span className="text-sm text-slate-500">jours après la suppression</span>
        </div>
        {invalid && <p className="text-xs text-red-600">Entre {MIN_DAYS} et {MAX_DAYS} jours.</p>}
        <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4 border-t border-slate-100 pt-3">
          <li>Passé ce délai, l'élément est effacé définitivement et ne peut plus être restauré.</li>
          <li>Un changement ne vaut que pour ce qui est supprimé ensuite : chaque élément déjà dans la corbeille garde sa date d'effacement.</li>
        </ul>
      </div>
      <SaveBar dirty={dirty} saveDisabled={invalid} saving={saving} canManage={canManage} onSave={save} onRevert={() => apply(saved)} />
    </div>
  );
}
